<template>
  <Transition name="loading-fade">
    <div v-if="isVisible" class="loading-overlay" :class="{ 'full-screen': fullScreen }">
      <div class="loading-container">
        <!-- Spinner -->
        <div class="loading-spinner"></div>
        
        <!-- Message -->
        <div class="loading-message">
          {{ message }}
        </div>
        
        <!-- Progress bar (optional) -->
        <div v-if="showProgress" class="loading-progress">
          <div class="progress-bar" :style="{ width: `${progress}%` }"></div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { EventBus } from '@/lib/eventbus.js'

export default {
  name: 'LoadingSpinner',
  props: {
    fullScreen: {
      type: Boolean,
      default: false
    },
    defaultMessage: {
      type: String,
      default: 'Cargando...'
    }
  },

  setup(props) {
    const isVisible = ref(false)
    const message = ref(props.defaultMessage)
    const progress = ref(0)
    const showProgress = ref(false)

    // Messages for different sections
    const loadingMessages = {
      'facturas': 'Cargando facturas...',
      'facturasm': 'Cargando facturas...',
      'albaranes': 'Cargando albaranes...',
      'albaranesm': 'Cargando albaranes...',
      'presupuestos': 'Cargando presupuestos...',
      'presupuestosm': 'Cargando presupuestos...',
      'clientes': 'Cargando clientes...',
      'clientesm': 'Cargando clientes...',
      'entregas': 'Cargando entregas...',
      'entregasm': 'Cargando entregas...',
      'articulos': 'Cargando artículos...',
      'articulosm': 'Cargando artículos...',
      'familias': 'Cargando familias...',
      'familiasm': 'Cargando familias...',
      'caja': 'Cargando caja...',
      'cajasm': 'Cargando caja...'
    }

    // State management
    let loadingTimeout = null

    // Event handlers
    const handleShowLoading = (data = {}) => {
      // Clear any existing timeout
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
        loadingTimeout = null
      }
      
      const section = data.section || 'default'
      const newMessage = loadingMessages[section] || data.message || props.defaultMessage
      
      // console.log('🔄 Loading SHOW:', { section, message: newMessage })
      
      message.value = newMessage
      
      if (data.progress !== undefined) {
        progress.value = data.progress
        showProgress.value = true
      } else {
        showProgress.value = false
      }
      
      // SIEMPRE mostrar, sin condiciones
      isVisible.value = true
      // console.log('🔄 Loading ahora visible:', isVisible.value)
    }

    const handleHideLoading = () => {
      // console.log('🔄 Loading HIDE')
      
      // Clear timeout if exists
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
        loadingTimeout = null
      }
      
      isVisible.value = false
      
      // Reset after transition
      setTimeout(() => {
        message.value = props.defaultMessage
        progress.value = 0
        showProgress.value = false
        // console.log('🔄 Loading reset completo')
      }, 300)
    }

    const handleUpdateProgress = (data) => {
      if (data.progress !== undefined) {
        progress.value = Math.min(100, Math.max(0, data.progress))
        showProgress.value = true
      }
      if (data.message) {
        message.value = data.message
      }
    }

    // Lifecycle
    onMounted(() => {
      // console.log('🔄 Loading component mounted')
      
      // FORZAR reset inicial
      isVisible.value = false
      message.value = props.defaultMessage
      progress.value = 0
      showProgress.value = false
      
      // Listen for global loading events
      EventBus.on('loading:show', handleShowLoading)
      EventBus.on('loading:hide', handleHideLoading)
      EventBus.on('loading:progress', handleUpdateProgress)
      EventBus.on('quitarloading', handleHideLoading)
      
      // console.log('🔄 Loading listeners registered')
    })

    onUnmounted(() => {
      // Clear any pending timeouts
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
      }
      
      EventBus.off('loading:show', handleShowLoading)
      EventBus.off('loading:hide', handleHideLoading)
      EventBus.off('loading:progress', handleUpdateProgress)
      EventBus.off('quitarloading', handleHideLoading)
    })

    // Public methods for direct control
    const show = (options = {}) => {
      handleShowLoading(options)
    }

    const hide = () => {
      handleHideLoading()
    }

    const updateProgress = (progressValue, messageText) => {
      handleUpdateProgress({ 
        progress: progressValue, 
        message: messageText 
      })
    }

    return {
      isVisible,
      message,
      progress,
      showProgress,
      show,
      hide,
      updateProgress
    }
  }
}
</script>

<style scoped>
/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.loading-overlay.full-screen {
  position: fixed;
}

.loading-overlay:not(.full-screen) {
  /* For content area only */
  position: absolute;
  top: 60px; /* Account for header height */
  left: 0;
}

/* Loading container */
.loading-container {
  text-align: center;
  padding: 2em;
  background: white;
  border-radius: 0.5em;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 300px;
  width: 90%;
}

/* Spinner */
.loading-spinner {
  width: 3em;
  height: 3em;
  border: 3px solid rgba(33, 133, 208, 0.2);
  border-top: 3px solid #2185d0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1em;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Loading message */
.loading-message {
  font-size: 1.1em;
  color: #2c3e50;
  margin-bottom: 1em;
  font-weight: 500;
}

/* Progress bar */
.loading-progress {
  width: 100%;
  height: 4px;
  background: rgba(33, 133, 208, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #2185d0;
  transition: width 0.3s ease;
  border-radius: 2px;
}

/* Transitions */
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: all 0.3s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.loading-fade-enter-to,
.loading-fade-leave-from {
  opacity: 1;
  backdrop-filter: blur(2px);
}

.loading-fade-enter-active .loading-container,
.loading-fade-leave-active .loading-container {
  transition: all 0.3s ease;
}

.loading-fade-enter-from .loading-container,
.loading-fade-leave-to .loading-container {
  transform: scale(0.8);
  opacity: 0;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .loading-overlay {
    background: rgba(0, 0, 0, 0.8);
  }
  
  .loading-container {
    background: #2c3e50;
    color: white;
  }
  
  .loading-message {
    color: #ecf0f1;
  }
  
  .loading-spinner {
    border: 3px solid rgba(52, 152, 219, 0.2);
    border-top: 3px solid #3498db;
  }
  
  .loading-progress {
    background: rgba(52, 152, 219, 0.2);
  }
  
  .progress-bar {
    background: #3498db;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .loading-container {
    padding: 1.5em;
    max-width: 280px;
  }
  
  .loading-spinner {
    width: 2.5em;
    height: 2.5em;
  }
  
  .loading-message {
    font-size: 1em;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
    border: 3px solid #2185d0;
  }
  
  .loading-fade-enter-active,
  .loading-fade-leave-active,
  .loading-fade-enter-active .loading-container,
  .loading-fade-leave-active .loading-container {
    transition: none;
  }
  
  .progress-bar {
    transition: none;
  }
}
</style>